// Install Prompt Service
// Handles PWA installation prompts and beforeinstallprompt events

export interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export interface InstallPromptOptions {
  title?: string;
  description?: string;
  icon?: string;
  buttonText?: string;
  showImmediately?: boolean;
  autoShow?: boolean;
  delay?: number;
}

export interface InstallPromptData {
  isSupported: boolean;
  canInstall: boolean;
  isInstalled: boolean;
  isStandalone: boolean;
  deferredPrompt: InstallPromptEvent | null;
}

export class InstallPromptService {
  private deferredPrompt: InstallPromptEvent | null = null;
  private isSupported: boolean;
  private isInstalled: boolean;
  private isStandalone: boolean;
  private listeners: Array<(data: InstallPromptData) => void> = [];
  private autoShowEnabled: boolean = false;
  private showDelay: number = 3000; // 3 seconds delay

  constructor() {
    this.isSupported = this.checkSupport();
    this.isInstalled = this.checkIfInstalled();
    this.isStandalone = this.checkIfStandalone();
    this.init();
  }

  /**
   * Check if PWA installation is supported
   */
  private checkSupport(): boolean {
    return 'serviceWorker' in navigator && 
           'PushManager' in window &&
           window.matchMedia('(display-mode: standalone)').matches === false;
  }

  /**
   * Check if app is already installed
   */
  private checkIfInstalled(): boolean {
    return window.matchMedia('(display-mode: standalone)').matches ||
           (window.navigator as any).standalone === true ||
           document.referrer.includes('android-app://');
  }

  /**
   * Check if app is running in standalone mode
   */
  private checkIfStandalone(): boolean {
    return window.matchMedia('(display-mode: standalone)').matches ||
           (window.navigator as any).standalone === true;
  }

  /**
   * Initialize the install prompt service
   */
  private init(): void {
    if (!this.isSupported) {
      console.warn('PWA installation is not supported in this browser');
      return;
    }

    // Listen for beforeinstallprompt event
    window.addEventListener('beforeinstallprompt', this.handleBeforeInstallPrompt.bind(this));
    
    // Listen for appinstalled event
    window.addEventListener('appinstalled', this.handleAppInstalled.bind(this));
    
    // Listen for display mode changes
    window.matchMedia('(display-mode: standalone)').addEventListener('change', this.handleDisplayModeChange.bind(this));

    console.log('Install prompt service initialized');
  }

  /**
   * Handle beforeinstallprompt event
   */
  private handleBeforeInstallPrompt(event: Event): void {
    // Prevent the mini-infobar from appearing on mobile
    event.preventDefault();
    
    // Store the event so it can be triggered later
    this.deferredPrompt = event as InstallPromptEvent;
    
    console.log('Install prompt event captured');
    
    // Notify listeners
    this.notifyListeners();
    
    // Auto-show if enabled
    if (this.autoShowEnabled) {
      setTimeout(() => {
        this.showInstallPrompt();
      }, this.showDelay);
    }
  }

  /**
   * Handle appinstalled event
   */
  private handleAppInstalled(event: Event): void {
    console.log('PWA was installed');
    this.isInstalled = true;
    this.deferredPrompt = null;
    
    // Notify listeners
    this.notifyListeners();
    
    // Clean up any install prompts
    this.cleanupInstallPrompts();
  }

  /**
   * Handle display mode changes
   */
  private handleDisplayModeChange(event: MediaQueryListEvent): void {
    this.isStandalone = event.matches;
    this.isInstalled = event.matches;
    
    if (event.matches) {
      console.log('App is now running in standalone mode');
    }
    
    // Notify listeners
    this.notifyListeners();
  }

  /**
   * Show the install prompt
   */
  async showInstallPrompt(): Promise<boolean> {
    if (!this.deferredPrompt) {
      console.warn('No install prompt available');
      return false;
    }

    if (this.isInstalled) {
      console.warn('App is already installed');
      return false;
    }

    try {
      // Show the install prompt
      this.deferredPrompt.prompt();
      
      // Wait for the user to respond to the prompt
      const choiceResult = await this.deferredPrompt.userChoice;
      
      if (choiceResult.outcome === 'accepted') {
        console.log('User accepted the install prompt');
        this.deferredPrompt = null;
        return true;
      } else {
        console.log('User dismissed the install prompt');
        return false;
      }
    } catch (error) {
      console.error('Error showing install prompt:', error);
      return false;
    }
  }

  /**
   * Enable auto-show of install prompt
   */
  enableAutoShow(delay: number = 3000): void {
    this.autoShowEnabled = true;
    this.showDelay = delay;
    console.log(`Auto-show enabled with ${delay}ms delay`);
  }

  /**
   * Disable auto-show of install prompt
   */
  disableAutoShow(): void {
    this.autoShowEnabled = false;
    console.log('Auto-show disabled');
  }

  /**
   * Check if install prompt can be shown
   */
  canShowInstallPrompt(): boolean {
    return this.isSupported && 
           !this.isInstalled && 
           !!this.deferredPrompt;
  }

  /**
   * Get current install prompt data
   */
  getInstallPromptData(): InstallPromptData {
    return {
      isSupported: this.isSupported,
      canInstall: this.canShowInstallPrompt(),
      isInstalled: this.isInstalled,
      isStandalone: this.isStandalone,
      deferredPrompt: this.deferredPrompt
    };
  }

  /**
   * Subscribe to install prompt changes
   */
  subscribe(callback: (data: InstallPromptData) => void): () => void {
    this.listeners.push(callback);
    
    // Immediately call with current data
    callback(this.getInstallPromptData());
    
    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(callback);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  /**
   * Notify all listeners of changes
   */
  private notifyListeners(): void {
    const data = this.getInstallPromptData();
    this.listeners.forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        console.error('Error in install prompt listener:', error);
      }
    });
  }

  /**
   * Clean up install prompts
   */
  private cleanupInstallPrompts(): void {
    // Remove any existing install prompts from the DOM
    const installPrompts = document.querySelectorAll('.install-prompt, .pwa-install-banner');
    installPrompts.forEach(prompt => {
      prompt.remove();
    });
  }

  /**
   * Get installation statistics
   */
  getInstallStats(): {
    isSupported: boolean;
    isInstalled: boolean;
    isStandalone: boolean;
    canInstall: boolean;
    hasPrompt: boolean;
    autoShowEnabled: boolean;
    showDelay: number;
  } {
    return {
      isSupported: this.isSupported,
      isInstalled: this.isInstalled,
      isStandalone: this.isStandalone,
      canInstall: this.canShowInstallPrompt(),
      hasPrompt: !!this.deferredPrompt,
      autoShowEnabled: this.autoShowEnabled,
      showDelay: this.showDelay
    };
  }

  /**
   * Reset the service state
   */
  reset(): void {
    this.deferredPrompt = null;
    this.autoShowEnabled = false;
    this.showDelay = 3000;
    this.notifyListeners();
  }

  /**
   * Check if running on mobile device
   */
  isMobileDevice(): boolean {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  }

  /**
   * Check if running on iOS
   */
  isIOS(): boolean {
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
  }

  /**
   * Check if running on Android
   */
  isAndroid(): boolean {
    return /Android/.test(navigator.userAgent);
  }

  /**
   * Get platform-specific install instructions
   */
  getInstallInstructions(): string {
    if (this.isIOS()) {
      return 'Tap the Share button and then "Add to Home Screen"';
    } else if (this.isAndroid()) {
      return 'Tap the menu button and then "Add to Home Screen"';
    } else {
      return 'Click the install button in your browser\'s address bar';
    }
  }

  /**
   * Show custom install banner
   */
  showCustomInstallBanner(options: InstallPromptOptions = {}): void {
    if (this.isInstalled) {
      return;
    }

    const banner = document.createElement('div');
    banner.className = 'pwa-install-banner';
    banner.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      padding: 16px;
      z-index: 9999;
      display: flex;
      align-items: center;
      justify-content: space-between;
      box-shadow: 0 2px 10px rgba(0,0,0,0.1);
      animation: slideDown 0.3s ease-out;
    `;

    const content = document.createElement('div');
    content.innerHTML = `
      <div style="display: flex; align-items: center; gap: 12px;">
        <img src="${options.icon || '/assets/icons/android-icon-48x48.png'}" 
             alt="App Icon" 
             style="width: 32px; height: 32px; border-radius: 8px;">
        <div>
          <div style="font-weight: 600; margin-bottom: 2px;">
            ${options.title || 'Install eSpa247 POS'}
          </div>
          <div style="font-size: 14px; opacity: 0.9;">
            ${options.description || 'Get quick access to the app from your home screen'}
          </div>
        </div>
      </div>
    `;

    const button = document.createElement('button');
    button.textContent = options.buttonText || 'Install';
    button.style.cssText = `
      background: white;
      color: #667eea;
      border: none;
      padding: 8px 16px;
      border-radius: 20px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s ease;
    `;

    button.addEventListener('click', () => {
      this.showInstallPrompt();
      banner.remove();
    });

    const closeButton = document.createElement('button');
    closeButton.innerHTML = '&times;';
    closeButton.style.cssText = `
      background: none;
      border: none;
      color: white;
      font-size: 20px;
      cursor: pointer;
      padding: 0;
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
    `;

    closeButton.addEventListener('click', () => {
      banner.remove();
    });

    banner.appendChild(content);
    banner.appendChild(button);
    banner.appendChild(closeButton);

    // Add CSS animation
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideDown {
        from { transform: translateY(-100%); }
        to { transform: translateY(0); }
      }
    `;
    document.head.appendChild(style);

    document.body.appendChild(banner);

    // Auto-remove after 10 seconds
    setTimeout(() => {
      if (banner.parentNode) {
        banner.remove();
      }
    }, 10000);
  }
}

// Create singleton instance
export const installPromptService = new InstallPromptService();
